import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

/**
 * Mevcut kullanıcının görebileceği çalışan kimliklerini hesaplar.
 * Operasyon Yönetimi ve SMO Lead Raporları ile aynı kurallar:
 *
 *  - admin (isAdmin=true) → kısıtlama yok (null döner)
 *  - SMO Lead            → merkez sahipliği (Centers.SMO === employeeId) üzerinden,
 *                          o merkezlere atanmış tüm çalışanlar
 *  - Bölge Yöneticisi    → Regions koleksiyonunda managerId/cityManagerIds eşleşmesi;
 *                          bölge illerindeki merkezlerin çalışanları
 *  - diğer herkes        → yalnızca kendisi
 *
 * Dönüş: null = kısıtlama yok; Set = izin verilen çalışan id'leri.
 * Liste çözülene kadar non-admin kullanıcılar için boş Set (güvenli taraf) döner.
 */
const useAllowedEmployeeIds = (isAdmin, employeeId) => {
  // null = kısıtlama yok (admin)
  const [allowedEmployeeIds, setAllowedEmployeeIds] = useState(null);

  useEffect(() => {
    // Admins see everyone — no restriction.
    if (isAdmin) {
      setAllowedEmployeeIds(null);
      return;
    }
    // Until we know who the user is, fail closed.
    if (!employeeId) {
      setAllowedEmployeeIds(new Set());
      return;
    }

    // Fail closed while the allowed list resolves.
    setAllowedEmployeeIds(new Set());

    const fetchAllowedEmployees = async () => {
      try {
        const [centersSnapshot, projectsSnapshot, regionsSnapshot] = await Promise.all([
          getDocs(collection(db, 'Centers')),
          getDocs(collection(db, 'Projects')),
          getDocs(collection(db, 'Regions')),
        ]);

        // centerId -> smoId, from the Centers collection
        const centerToSmoMap = {};
        centersSnapshot.docs.forEach((centerDoc) => {
          const smoId = centerDoc.data().SMO;
          if (smoId) centerToSmoMap[centerDoc.id] = smoId;
        });

        // Resolve each center's owning SMO, recovering deleted centers (not in
        // the Centers collection) the same way SMO Lead Reports does:
        //   1. an employee in that center carries an `smoId` field, or
        //   2. another center in the same project is owned by a known SMO.
        const centerOwner = {}; // centerId -> resolved smoId
        const processedCenters = new Set();
        projectsSnapshot.docs.forEach((projectDoc) => {
          const projectEmployees = projectDoc.data().employees || [];
          const centerEmployeesMap = {};
          projectEmployees.forEach((emp) => {
            if (!emp.centerId) return;
            if (!centerEmployeesMap[emp.centerId]) centerEmployeesMap[emp.centerId] = [];
            centerEmployeesMap[emp.centerId].push(emp);
          });

          for (const [centerId, centerEmployees] of Object.entries(centerEmployeesMap)) {
            if (processedCenters.has(centerId)) continue;
            let smoId = centerToSmoMap[centerId];
            if (!smoId) {
              const empWithSmo = centerEmployees.find((e) => e.smoId);
              if (empWithSmo) smoId = empWithSmo.smoId;
              if (!smoId) {
                for (const otherId of Object.keys(centerEmployeesMap)) {
                  if (otherId !== centerId && centerToSmoMap[otherId]) {
                    smoId = centerToSmoMap[otherId];
                    break;
                  }
                }
              }
              if (!smoId) continue;
            }
            centerOwner[centerId] = smoId;
            processedCenters.add(centerId);
          }
        });

        // Check if user is Region Manager
        const normalizeCity = (city) => city.toLowerCase().trim()
          .replace(/ı/g, 'i')
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ş/g, 's')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c');

        let isRegionManager = false;
        const myRegionCenterIds = new Set();

        regionsSnapshot.docs.forEach((regionDoc) => {
          const regionData = regionDoc.data();
          const managerId = regionData.managerId;
          const cityManagerIds = regionData.cityManagerIds || {};

          // Check if user is the main region manager or a city manager
          if (managerId === employeeId || Object.values(cityManagerIds).includes(employeeId)) {
            isRegionManager = true;

            // Get all cities in this region
            const cities = regionData.cities || [];

            // Find all centers in these cities
            centersSnapshot.docs.forEach((centerDoc) => {
              const centerData = centerDoc.data();
              const centerCity = centerData['İl'] || '';

              if (cities.some(city => normalizeCity(city) === normalizeCity(centerCity))) {
                myRegionCenterIds.add(centerDoc.id);
              }
            });
          }
        });

        // If user is Region Manager, get all employees in their region's centers
        if (isRegionManager && myRegionCenterIds.size > 0) {
          const workerIds = new Set();
          projectsSnapshot.docs.forEach((projectDoc) => {
            const projectEmployees = projectDoc.data().employees || [];
            projectEmployees.forEach((emp) => {
              if (emp.id && emp.centerId && myRegionCenterIds.has(emp.centerId)) {
                workerIds.add(emp.id);
              }
            });
          });
          // The Region Manager themselves is always selectable
          workerIds.add(employeeId);
          setAllowedEmployeeIds(workerIds);
          return;
        }

        // Centers owned by the current user (resolved + direct ownership)
        const myCenterIds = new Set(
          Object.keys(centerOwner).filter((cid) => centerOwner[cid] === employeeId)
        );
        Object.keys(centerToSmoMap).forEach((cid) => {
          if (centerToSmoMap[cid] === employeeId) myCenterIds.add(cid);
        });

        // Not an SMO (owns no centers) → may only see themselves.
        if (myCenterIds.size === 0) {
          setAllowedEmployeeIds(new Set([employeeId]));
          return;
        }

        // SMO: employees assigned to those centers across all projects
        const workerIds = new Set();
        projectsSnapshot.docs.forEach((projectDoc) => {
          const projectEmployees = projectDoc.data().employees || [];
          projectEmployees.forEach((emp) => {
            if (emp.id && emp.centerId && myCenterIds.has(emp.centerId)) {
              workerIds.add(emp.id);
            }
          });
        });
        // The SMO themselves is always selectable.
        workerIds.add(employeeId);

        setAllowedEmployeeIds(workerIds);
      } catch (error) {
        console.error('Error computing allowed employees:', error);
        // Fail closed: restrict to self rather than exposing all employees
        setAllowedEmployeeIds(new Set([employeeId]));
      }
    };

    fetchAllowedEmployees();
  }, [isAdmin, employeeId]);

  return allowedEmployeeIds;
};

export default useAllowedEmployeeIds;
